/**
 * Candidate profiles on citizen surfaces (CANDIDATE_PROFILES_PRD.md §5.5,
 * §5.6.5): the published shapes the person and ballot payloads carry, and the
 * two pure rules every surface applies — the per-measure agree/differ tag
 * against the citizen's own pick (never summed, never stored), and the
 * 120-word expander threshold for verbatim answers (never a truncation).
 */
import type { MeasurePosition } from './useBallotPicks';
import type { EndorsementRow } from '../../constants/endorsements';

export type CoverageTier = 'full' | 'standard';
export type Stance = 'support' | 'oppose' | 'unclear' | 'declined' | 'no_position';
export type ResponseStatus = 'answered' | 'declined' | 'no_response';

export interface ProfileProvenance {
  origin: 'staff_written' | 'ai_reviewed' | 'official_source';
  tool?: string | null;
  source?: string | null;
  reviewed_at?: string | null;
}

/** The published profile on GET /people/:id → candidate.profile. */
export interface CandidateProfile {
  coverage_tier: CoverageTier;
  profile_version: string | null;
  facts_as_of: string | null;
  who_they_are: string | null;
  why_running: string | null;
  top_priorities: string | null;
  experience_background: string | null;
  record_in_office: string | null;
  who_backs_them: string | null;
  content_provenance: Partial<Record<string, ProfileProvenance>> | null;
}

/** The summary the assembled ballot carries per candidate (§5.5.3). */
export interface CandidateProfileSummary {
  coverage_tier: CoverageTier;
  facts_as_of: string | null;
  who_they_are: string | null;
  content_provenance: Partial<Record<string, ProfileProvenance>> | null;
}

export interface CandidateStatement {
  id: string;
  source_type: string;
  source_name: string;
  source_url: string | null;
  source_date: string | null;
  question: string | null;
  body: string | null;
  response_status: ResponseStatus;
  display_order: number;
}

/** The shared endorsement row (ENDORSEMENTS_PRD §5.1), as /api/people returns it. */
export type CandidateEndorsement = EndorsementRow;

export interface CandidateMeasurePosition {
  id: string;
  ballot_initiative_id: string;
  stance: Stance;
  quote: string | null;
  source_type: string | null;
  source_name: string | null;
  source_url: string | null;
  source_date: string;
  measure_number: string | null;
  popular_name: string | null;
  ballot_title: string | null;
  ballot_order: number | null;
  /** The measure's citizen-content summary (older payloads omit it). */
  plain_language_summary?: string | null;
}

/** A candidate's stance on one measure, as the ballot carries it per measure (§5.5.5). */
export interface MeasureCandidatePosition {
  candidate_id: string;
  person_id: string | null;
  name: string;
  party: string | null;
  office_sought: string | null;
  office_key: string | null;
  stance: Stance;
  quote: string | null;
  source_type: string | null;
  source_name: string | null;
  source_url: string | null;
  source_date: string;
}

export interface RunningMate {
  candidate_id: string;
  person_id: string;
  name: string;
  party: string | null;
  role: 'governor' | 'lt_governor';
}

export const COVERAGE_LABELS: Record<CoverageTier | 'official_only', string> = {
  full: 'Full profile',
  standard: 'Standard profile',
  official_only: 'Official record only',
};
export const coverageLabel = (tier: CoverageTier | null | undefined): string => COVERAGE_LABELS[tier ?? 'official_only'];

export const STANCE_LABELS: Record<Stance, string | null> = {
  support: 'Supports',
  oppose: 'Opposes',
  unclear: null,
  declined: 'Declined to say',
  no_position: 'No position found',
};

export const RESPONSE_STATUS_LABELS: Record<ResponseStatus, string> = {
  answered: 'Answered',
  declined: 'Declined to answer',
  no_response: 'No response',
};

export const SOURCE_TYPE_LABELS: Record<string, string> = {
  questionnaire: 'Questionnaire',
  website: 'Campaign site',
  statement: 'Statement',
  news_quote: 'Quoted in the press',
  referral_vote: 'Recorded vote',
};

/** The provenance label a reader sees; staff-written carries none. */
export function provenanceLabel(entry: ProfileProvenance | undefined | null): string | null {
  if (!entry) return null;
  if (entry.origin === 'ai_reviewed') return entry.tool ? `AI-assisted (${entry.tool}), staff-reviewed` : 'AI-assisted, staff-reviewed';
  if (entry.origin === 'official_source') return entry.source ? `Verbatim from ${entry.source}` : 'Verbatim from an official source';
  return null;
}

/**
 * Agrees / differs for ONE measure (O14): only against a taken stance, only
 * where the citizen marked that measure. Nothing here is ever summed.
 */
export type Agreement = 'agrees' | 'differs' | null;
export function agreement(pick: MeasurePosition | undefined, stance: Stance): Agreement {
  if (!pick) return null;
  if (stance !== 'support' && stance !== 'oppose') return null;
  const candidateYes = stance === 'support';
  return candidateYes === (pick === 'yes') ? 'agrees' : 'differs';
}

/** The vote line for a referral-vote row, or the quote for everything else. */
export function positionEvidence(p: { quote: string | null; source_type: string | null }): string | null {
  if (p.quote) return p.quote;
  if (p.source_type === 'referral_vote') return 'Voted on referring it to the ballot';
  return null;
}

/** Word count of plain text (HTML tags stripped) — for the 120-word expander (§5.5.1). */
export const EXPANDER_WORDS = 120;
export function wordCount(text: string | null | undefined): number {
  if (!text) return 0;
  const plain = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return plain ? plain.split(' ').length : 0;
}
export function needsExpander(text: string | null | undefined): boolean {
  return wordCount(text) > EXPANDER_WORDS;
}

/** First paragraph of a verbatim answer, for the collapsed state — never a truncation of a paragraph. */
export function firstParagraph(text: string): string {
  const idx = text.search(/\n\s*\n/);
  return idx > 0 ? text.slice(0, idx) : text;
}

/** Group statements by source (name + date), in display order. */
export function groupStatements(statements: CandidateStatement[]) {
  const groups = new Map<string, { source_name: string; source_url: string | null; source_date: string | null; rows: CandidateStatement[] }>();
  for (const s of statements) {
    const key = `${s.source_name}|${s.source_date ?? ''}`;
    if (!groups.has(key)) groups.set(key, { source_name: s.source_name, source_url: s.source_url, source_date: s.source_date, rows: [] });
    groups.get(key)!.rows.push(s);
  }
  return [...groups.values()];
}

/** Candidates on a measure grouped by stance for the measure-side block (§5.5.5). */
export function groupByStance(rows: MeasureCandidatePosition[]) {
  const out: Record<'support' | 'oppose' | 'no_position' | 'unclear' | 'declined', MeasureCandidatePosition[]> = {
    support: [], oppose: [], no_position: [], unclear: [], declined: [],
  };
  for (const r of rows) out[r.stance].push(r);
  return out;
}
